import { lazy, Suspense, useEffect, useState } from 'react';

import { Button, Input, Select, Spinner, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import ModalDelete from '@/components/Modals/ModalDelete';
import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, INSPECTION_PROCESS, ROLES } from '@/common/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataTotalNewInspectionProcess } from '@/redux/slices/notificationSlice';
import { deleteNomination, getListProcessAdmin, getListProcessInspectingCompany, getListProcessShipOperator } from '@/services/nominationService';
import { downloadFile } from '@/services/downloadService';
import { getNewRecordCounterInspectingCompany, getNewRecordCounterShipOperator, getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';

import '../styles/ListPage.css'
import apiCall from '@/services/apiCall';
import { downloadBlobResponse } from '@/services/blobDownloadService';
import { getDataRef } from '@/services/refDataService';
import { Option } from "@/utils/select";
import { getCurrentMonth } from '@/utils/date';
import useSmallScreen from '@/utils/UseDeviceSize';
import MonthSelector from '@/components/Dashboard/MonthSelector';

const TableInspectionProcess = lazy(() => import('@/components/Tables/TableInspectionProcess'));

const InspectionProcessList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const appId = useAppSelector((state: RootState) => state.account.appId);
    const dispatch = useAppDispatch();
    const isSmallScreen = useSmallScreen();

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [typeValue, setTypeValue] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [inspectionValue, setInspectionValue] = useState(null as string | null);
    const [listMarket, setListMarket] = useState<Option[]>([]);
    const [marketValue, setMarketValue] = useState('');
    const {year: currentYear, month: currentMonth} = getCurrentMonth();
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const showToast = (error: any) => {
        toast({
            title: error?.status || 'Error',
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        const obj = { inspectorId: idToDelete, modifiedBy: name || "System" };

        try {
            const response = await deleteNomination(obj) as any;
            if (response.status === HttpStatusCode.Ok) {
                getData();
                toast({
                    title: 'Nomination deleted.',
                    description: "We've deleted the nomination for you.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                showToast({ code: 'Delete Failed', message: "We failed to delete the nomination." });
            }
        } catch {
            showToast({ code: 'Delete Error', message: "An error occurred while deleting." });
        } finally {
            onClose();
        }
    };

    const handleSearch = () => {
        setCurrentPageNumber(1);
        setTimeout(() => {
            getData();
        }, 500);
    };

	const handleDownload = async (id: string) => {
		await downloadFile(id);
    };

    const handleCancelProcess = (id: string) => {
        navigate('/inspection-process/cancel', { state: { id, process: true } });
    }

    const setLatestCounterAdmin = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
        }
    }

    const setLatestCounterShipOperator = async () => {
        const responseCounter = await getNewRecordCounterShipOperator(appId) as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
        }
    }

    const setLatestCounterInspectingCompany = async () => {
        const responseCounter = await getNewRecordCounterInspectingCompany(appId) as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
        }
    }

    const getData = async () => {
        setIsLoading(true);
        const param = { search: searchValue, pageNumber: currentPageNumber, pageSize: currentPageSize, type: typeValue, inspectionStatus: inspectionValue, market: marketValue, year: year, month: month };

        try {
            let response = null as any;
            if (role === 'super admin') {
                response = await getListProcessAdmin(param) as any;
                setLatestCounterAdmin();
            } else if (role === 'inspecting company') {
                const newParam = { ...param, id: appId };
                response = await getListProcessInspectingCompany(newParam) as any;
                setLatestCounterInspectingCompany();
            } else if (role === 'ship operator') {
                const newParam = { ...param, id: appId };
                response = await getListProcessShipOperator(newParam) as any;
                setLatestCounterShipOperator();
            }

            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
            } else {
                showToast(response);
            }
        } catch {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportToExcel = async () => {
        const query = `?search=${searchValue}&type=${typeValue}&pageNumber=${currentPageNumber}&pageSize=${currentPageSize}&inspectionStatus=${inspectionValue}&market=${marketValue}&year=${year}&month=${month}`;
        const URL = `/Nominations/ExporttoExcelNominationsProcessAdmin${query}`;

        try {
            const response = await apiCall('get', URL, { responseType: 'blob' }) as any;
            await downloadBlobResponse(response, showToast, 'Inspection Process.xlsx');
        } catch {
            showToast({ code: 'Error', message: 'Failed to export data' });
        }
    };

    const getListRef = async (type: string) => {
        const response = await getDataRef(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.value,
                label: item.name,
            }));

            if (type === 'market') {
                setListMarket(formattedData);
            }
        } else {
            toast({
                title: 'Failed to get data ref.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListMarket([]);
        }
    }

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await Promise.all([
                    getListRef('market'),
                ])

            } catch (_error) {
                console.log(_error);
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className='table-filter-input filter-input-width-6'>
                            <Input
                                placeholder="Search"
                                style={{ backgroundColor: 'white' }}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                        <div className='table-filter-input filter-input-width-3'>
                            <Select
                                placeholder="All Type"
                                style={{ backgroundColor: 'white' }}
                                onChange={(e) => setTypeValue(e.target.value)}
                            >
                                <option value="sire">SIRE</option>
                                <option value="bire">BIRE</option>
                                <option value="vetting">Vetting</option>
                            </Select>
                        </div>
                        {[ROLES.SUPER_ADMIN].includes(role) && (
                            <>
                                <div className='table-filter-input filter-input-width-3'>
                                    <Select
                                        placeholder="All Status Inspection"
                                        style={{ backgroundColor: 'white' }}
                                        onChange={(e) => setInspectionValue(e.target.value)}
                                    >
                                        <option value={INSPECTION_PROCESS.IN_PROGRESS}>In Progress</option>
                                        <option value={INSPECTION_PROCESS.DONE}>Done</option>
                                        <option value={INSPECTION_PROCESS.CANCEL}>Cancel</option>
                                    </Select>
                                </div>
                                <div className='table-filter-input filter-input-width-3'>
                                    <Select
                                        placeholder="All Market"
                                        onChange={(e) => setMarketValue(e.target.value)}

                                    >
                                        {listMarket.map((market) => (
                                            <option key={market.value} value={market.value}>
                                                {market.label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </>
                        )}
                        <MonthSelector month={month} year={year} onChange={(newMonth, newYear) => { setMonth(newMonth); setYear(newYear) }} isStacked={isSmallScreen}  />
                        <Button
                            px={8}
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            borderRadius={10}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                        {[ROLES.SUPER_ADMIN].includes(role) && (
                            <Button
                                className='export-to-excel-button'
                                bgColor={COLORS.BUTTON.BACKGROUND}
                                color={COLORS.BUTTON.FOREGROUND}
                                borderRadius={10}
                                onClick={handleExportToExcel}
                            >
                                Export to Excel
                            </Button>
                        )}
                    </div>
                </div>
                <Suspense fallback={<Spinner size="xl" />}>
                    <TableInspectionProcess
                        role={role}
                        dataList={dataList}
                        isLoading={isLoading}
                        handleEdit={(id: string) => navigate('/inspection-process/edit', { state: { id } })}
                        handleSubmitResult={(id: string) => navigate('/inspection-process/submit-result', { state: { id } })}
                        handleCreateInvoiceInspectingCompany={(id: string, sequenceId: string) => navigate('/inspection-process/create-invoice', { state: { id, sequenceId } })}
                        handleDelete={handleDelete}
                        handleDownload={handleDownload}
                        handleCancelProcess={handleCancelProcess}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    );
};

export default InspectionProcessList;
